/**
 * Essay card component for displaying essay list items.
 * 
 * Features:
 * - Title with link to full essay
 * - Status badge (draft, in_review, etc.) using StatusBadge component
 * - Content preview
 * - Updated timestamp
 * 
 * Called by:
 * - EssaysPage.tsx to render essay list
 */
import { Link } from 'react-router-dom';
import { Card, Typography, Space } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import type { EssayListItem } from '@compendium/shared';
import StatusBadge from './StatusBadge';

const { Title, Paragraph, Text } = Typography;

/**
 * Props for EssayCard component.
 */
interface EssayCardProps {
  /** Essay data to display */
  essay: EssayListItem;
}

/**
 * Essay card component.
 * Displays essay summary with link to full content.
 */
function EssayCard({ essay }: EssayCardProps) {
  /**
   * Format date for display.
   * Shows relative time if recent, otherwise full date.
   */
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }

  return (
    <Link to={`/reviewer/essays/${essay.slug}`} style={{ display: 'block' }}>
      <style>{`
        .essay-card:hover {
          border-left-color: #d4af37 !important;
          box-shadow: 0 2px 12px rgba(212, 175, 55, 0.1) !important;
        }
      `}</style>
      <Card
        hoverable
        style={{
          backgroundColor: '#1a1a1a',
          borderColor: '#333',
          borderLeft: '3px solid transparent',
          borderLeftColor: essay.status === 'published' ? '#d4af37' : essay.status === 'draft' ? '#555' : 'transparent',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
        className="essay-card"
        styles={{
          body: { padding: '20px' },
        }}
      >
        {/* Header with title and status */}
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title
              level={4}
              style={{
                margin: 0,
                color: '#f5f5f5',
                fontWeight: 500,
              }}
            >
              {essay.title}
            </Title>
            <StatusBadge status={essay.status} />
          </Space>

          {/* Preview content */}
          <Paragraph
            style={{
              color: '#a0a0a0',
              marginBottom: '12px',
            }}
            ellipsis={{ rows: 2 }}
          >
            {essay.preview}
          </Paragraph>

          {/* Footer with timestamp */}
          <Space size="small">
            <ClockCircleOutlined style={{ color: '#a08a3a' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Updated {formatDate(essay.updatedAt)}
            </Text>
          </Space>
        </Space>
      </Card>
    </Link>
  );
}

export default EssayCard;
